import { Module } from 'vuex'
import { RootState } from '@/store'
import { actions } from '@/store/smartMarketing/actions'
import { mutations } from '@/store/smartMarketing/mutations'
import { getters } from '@/store/smartMarketing/getters'
import { Company } from '@/api/model/company'
import { User } from '@/api/model/user'
import { CredentialsType, EmmaDefaults, ReferralStatus } from '@/api/types'
import { RealworksKenmerk } from '@/api/model/realworks-kenmerk'

export interface SmartMarketingState {
    loading: boolean
    saving: boolean
    activating: boolean
    validating: boolean
    valid: boolean
    error: string
    loadingContactsCount: boolean
    contactsCount: number
    loadingRealworksKenmerken: boolean
    realworksKenmerken: RealworksKenmerk[]
    loadingRealworksRelatietypes: boolean
    realworksRelatietypes: string[]
    loadingRealworksAgendatypes: boolean
    realworksAgendaEnabled: boolean
    realworksAgendatypes: string[]
    loadingRealworksTaaktypes: boolean
    realworksTaakEnabled: boolean
    realworksTaaktypes: string[]

    // Credentials
    company: Company|null
    id: string
    enabled: boolean
    createdAt: Date
    createdBy: User|null
    updatedAt: Date
    updatedBy: User|null
    credentialsType: CredentialsType

    // Shared
    includeInternalContacts: boolean
    includeExternalContacts: boolean
    includeRealworksRelaties: boolean
    syncedRealworksKenmerken: string[]
    excludedRealworksKenmerken: string[]
    syncedRealworksRelatietypes: string[]
    syncedRealworksStatus: boolean|null
    updatedFrom: Date
    updatedUntil: Date
    apiKey: string|null
    listId: string|null
    agendaListId: string|null
    taakListId: string|null
    viewingListId: string|null
    emmaListId: string|null
    emmaDefaults: EmmaDefaults

    // ActiveCampaignCredentials
    apiUrl: string|null
    eventTrackingEnabled: boolean
    eventTrackingAccountId: string|null
    eventTrackingKey: string|null
    defaultTeams: object
    defaultStatus: string
    syncedRealworksAgendatypes: string[]
    syncedRealworksTaaktypes: string[]

    // MailchimpCredentials
    server: string|null
}

const state: SmartMarketingState = {
    loading: false,
    saving: false,
    activating: false,
    validating: false,
    valid: false,
    error: null,
    loadingContactsCount: false,
    contactsCount: 0,
    loadingRealworksKenmerken: false,
    realworksKenmerken: [],
    loadingRealworksRelatietypes: false,
    realworksRelatietypes: [],
    loadingRealworksAgendatypes: false,
    realworksAgendaEnabled: false,
    realworksAgendatypes: [],
    loadingRealworksTaaktypes: false,
    realworksTaakEnabled: false,
    realworksTaaktypes: [],

    // Credentials
    company: null,
    id: null,
    enabled: false,
    createdAt: null,
    createdBy: null,
    updatedAt: null,
    updatedBy: null,
    credentialsType: CredentialsType.ACTIVE_CAMPAIGN,

    // Shared
    includeInternalContacts: false,
    includeExternalContacts: false,
    includeRealworksRelaties: false,
    syncedRealworksKenmerken: [],
    excludedRealworksKenmerken: [],
    syncedRealworksRelatietypes: [],
    syncedRealworksStatus: true,
    updatedFrom: null,
    updatedUntil: null,
    apiKey: null,
    listId: null,
    agendaListId: null,
    taakListId: null,
    viewingListId: null,
    emmaListId: null,
    emmaDefaults: {},

    // ActiveCampaignCredentials
    apiUrl: null,
    eventTrackingEnabled: false,
    eventTrackingAccountId: null,
    eventTrackingKey: null,
    defaultTeams: [],
    defaultStatus: ReferralStatus.PROCESSED,
    syncedRealworksAgendatypes: [],
    syncedRealworksTaaktypes: [],

    // MailchimpCredentials
    server: null,
}

export const SmartMarketingModule: Module<SmartMarketingState, RootState> = {
    namespaced: true,
    state,
    actions,
    mutations,
    getters,
}
